import { TrendingUp, TrendingDown, Minus, Target, ShieldAlert, DollarSign, BarChart3, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { motion } from "framer-motion";

export default function AnalysisResults({ analysis }) {
    if (!analysis) return null;

    const getSignalColor = (signal) => {
        switch (signal) {
            case "BUY":
                return "from-green-600 to-emerald-600";
            case "SELL":
                return "from-red-600 to-rose-600";
            default:
                return "from-gray-600 to-gray-700";
        }
    };

    const getSignalIcon = (signal) => {
        switch (signal) {
            case "BUY":
                return <TrendingUp className="w-6 h-6" />;
            case "SELL":
                return <TrendingDown className="w-6 h-6" />;
            default:
                return <Minus className="w-6 h-6" />;
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
        >
            {/* Signal Header */}
            <Card className="border-gray-800 bg-zinc-950/80 backdrop-blur overflow-hidden relative shadow-2xl">
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getSignalColor(analysis.signal)}`} />
                <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${getSignalColor(analysis.signal)} flex items-center justify-center text-white shadow-lg`}>
                                {getSignalIcon(analysis.signal)}
                            </div>
                            <div>
                                <CardTitle className="text-3xl font-bold text-white mb-1">
                                    {analysis.signal}
                                </CardTitle>
                                <p className="text-amber-500 text-lg font-semibold">{analysis.pair}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-400 mb-2">
                                {analysis.confidence}
                            </Badge>
                            <p className="text-xs text-gray-500">{analysis.analysis_type}</p>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="bg-black/50 rounded-xl p-4 border border-gray-900">
                        <p className="text-gray-300 leading-relaxed">{analysis.explanation}</p>
                    </div>
                </CardContent>
            </Card>

            {/* Trade Levels Grid */}
            <div className="grid md:grid-cols-2 gap-4">
                {/* Entry & Stop Loss */}
                <Card className="border-gray-800 bg-zinc-950/80 backdrop-blur shadow-xl">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-amber-500" />
                            Entry & Risk Management
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {analysis.entry_price && (
                            <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-lg border border-gray-800">
                                <span className="text-gray-400">Entry Price</span>
                                <span className="text-white font-bold text-lg">{analysis.entry_price}</span>
                            </div>
                        )}
                        {analysis.stop_loss && (
                            <div className="flex justify-between items-center p-4 bg-red-950/20 rounded-lg border border-red-900/30">
                                <span className="text-red-300 flex items-center gap-2">
                                    <ShieldAlert className="w-4 h-4" />
                                    Stop Loss
                                </span>
                                <span className="text-red-400 font-bold text-lg">{analysis.stop_loss}</span>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Take Profits */}
                <Card className="border-gray-800 bg-zinc-950/80 backdrop-blur shadow-xl">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Target className="w-5 h-5 text-amber-500" />
                            Take Profit Levels
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {analysis.take_profit_1 && (
                            <div className="flex justify-between items-center p-4 bg-emerald-950/20 rounded-lg border border-emerald-900/30">
                                <span className="text-emerald-300 font-semibold">TP1</span>
                                <span className="text-emerald-400 font-bold">{analysis.take_profit_1}</span>
                            </div>
                        )}
                        {analysis.take_profit_2 && (
                            <div className="flex justify-between items-center p-4 bg-emerald-950/20 rounded-lg border border-emerald-900/30">
                                <span className="text-emerald-300 font-semibold">TP2</span>
                                <span className="text-emerald-400 font-bold">{analysis.take_profit_2}</span>
                            </div>
                        )}
                        {analysis.take_profit_3 && (
                            <div className="flex justify-between items-center p-4 bg-emerald-950/20 rounded-lg border border-emerald-900/30">
                                <span className="text-emerald-300 font-semibold">TP3</span>
                                <span className="text-emerald-400 font-bold">{analysis.take_profit_3}</span>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Additional Info */}
            {analysis.timeframe && (
                <Card className="border-gray-800 bg-zinc-950/80 backdrop-blur shadow-xl">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-center gap-8 text-gray-400">
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-amber-500" />
                                <span>Timeframe: <span className="text-white font-semibold">{analysis.timeframe}</span></span>
                            </div>
                            <div className="h-4 w-px bg-gray-800" />
                            <div className="flex items-center gap-2">
                                <BarChart3 className="w-4 h-4 text-amber-500" />
                                <span><span className="text-white font-semibold">{analysis.analysis_type}</span> Analysis</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </motion.div>
    );
}
